import { createSelector } from 'reselect';
import { OrderedMap, OrderedSet } from 'immutable';

import type { State } from 'reducers';
import type { DonationState } from 'entities/donation/reducer';
import Donation from 'entities/donation/model';

export const selectDonationsState = (state: State): DonationState => state.get('entities').donation;

export const sortNewestFirstForProperty =
  (dateProperty: 'createdAt' | 'matchedAt' | 'date' | 'matchRequestRejectedAt') =>
  (a: Donation, b: Donation): number => {
    const aDate = a[dateProperty];
    const bDate = b[dateProperty];
    if (bDate && aDate) {
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    }
    if (bDate) {
      return 1;
    }
    if (aDate) {
      return -1;
    }
    return 0;
  };

const sortNewestFirst = sortNewestFirstForProperty('date');
export const sortNewestCreatedFirst = sortNewestFirstForProperty('createdAt');
export const sortNewestMatchedFirst = sortNewestFirstForProperty('matchedAt');
export const sortNewestMatchRequestRejectedFirst =
  sortNewestFirstForProperty('matchRequestRejectedAt');

export const selectDonations = createSelector<State, OrderedMap<string, Donation>, DonationState>(
  selectDonationsState,
  (donationsState) => donationsState.get('donations')
);

export const selectDisplayedDonations = createSelector(
  selectDonations,
  selectDonationsState,
  (donations: OrderedMap<string, Donation>, donationsState) => {
    const displayedDonationIds: OrderedSet<string> = donationsState.get('displayedDonationIds');
    return displayedDonationIds
      .map((donationId) => donationId && donations.get(donationId))
      .filter((donation) => !!donation && donation.status !== 'Pending');
  }
);

export const selectExternalDonations = createSelector(selectDonations, (donations) =>
  donations.filter((donation) => !!donation && donation.isExternal())
);

export const selectPendingExternalDonations = createSelector(selectExternalDonations, (donations) =>
  donations
    .filter((donation) => !!donation && donation.matchingRequestStatus === 'Pending')
    .sort(sortNewestCreatedFirst)
);

export const selectAcceptedExternalDonations = createSelector(
  selectExternalDonations,
  (donations) =>
    donations
      .filter((donation) => !!donation && donation.matchingRequestStatus === 'Approved')
      .sort(sortNewestMatchedFirst)
);

export const selectDeniedExternalDonations = createSelector(selectExternalDonations, (donations) =>
  donations
    .filter((donation) => !!donation && donation.matchingRequestStatus === 'Rejected')
    .sort(sortNewestMatchRequestRejectedFirst)
);

export const selectFetchParams = createSelector(selectDonationsState, (state) =>
  state.get('fetchParams')
);

export const selectTotal = createSelector(selectDonationsState, (donationsState) =>
  donationsState.get('total')
);

export const selectPendingExternalTotal = createSelector(selectDonationsState, (donationsState) =>
  donationsState.get('pendingExternalTotal')
);

export const selectDonationById = createSelector(
  selectDonations,
  (_donations, donationId) => donationId,
  (donations, donationId) => donations.get(donationId)
);

export const selectDonationsForUser = createSelector(
  selectDonations,
  (_donations, user) => user,
  (donations, user) =>
    donations.filter((donation) => !!donation && donation.isByUser(user)).sort(sortNewestFirst)
);

export const selectDonationsLoaded = createSelector(
  selectDonationsState,
  (_donationsState, userId) => userId,
  (donationsState, userId) => donationsState.get('donationsLoaded').has(userId) || false
);

export const selectDonationsForPayrollRun = createSelector(
  selectDonations,
  (_donations, payrollRun) => payrollRun,
  (donations, payrollRun) =>
    donations.filter((donation) => !!donation && donation.payrollRun?._id === payrollRun)
);
